// Library methods
import { useTranslation } from "react-i18next";
import React, { useCallback } from "react";

// MUI Components
import { Grid, Typography } from "@mui/material";

// Utilities
import styles from "../index.module.css";

const BaileyLovieGraph = ({ examData }) => {
  const { t } = useTranslation();

  const getChartTitle = useCallback(
    (chartContrast) => {
      // if the contrast isn't in the 0 - 100 range,
      // display error text
      if (
        typeof chartContrast !== "number" ||
        chartContrast > 100 ||
        chartContrast < 0
      ) {
        return "N/A";
      }

      // else return a title based on the contrast value
      if (chartContrast >= 50) {
        return `${t("word_high_contrast")} (${chartContrast}%)`;
      }
      return `${t("word_low_contrast")} (${chartContrast}%)`;
    },
    [t]
  );

  const getChartLetters = useCallback((chartRows, chartContrast) => {
    let displayedLetters = [];

    chartRows?.forEach((chartRow) => {
      displayedLetters.push(
        chartRow?.letters?.map((char) => ({
          color:
            char?.State === "GuessedCorrectly"
              ? "#13A321"
              : char?.State === "None" || char?.State === "Skipped"
                ? `#202338${chartContrast}`
                : "#B21515",
          backgroundImage:
            char?.State === "Skipped"
              ? "linear-gradient(0deg, #E7C43C60 25%, #FFF 25%, #FFF 50%, #E7C43C60 50%, #E7C43C60 75%, #FFF 75%, #FFF 100%)"
              : "none",
          backgroundSize: "8px 8px",
          value: char?.Value,
        }))
      );
    });

    return displayedLetters;
  }, []);

  return (
    <Grid
      container
      item
      xs={12}
      display="flex"
      flexWrap="wrap"
      rowGap="20px"
      className={
        examData?.state?.charts?.length > 1
          ? "retinalogik-va-multichart"
          : undefined
      }
    >
      {examData?.state?.charts?.map((chart, index, array) => {
        const chartRows = chart?.rows ?? [];
        const chartContrast = Number(chart?.contrast) * 100;
        const chartLetters = getChartLetters(chartRows, chartContrast);
        const chartTitle = getChartTitle(chartContrast);

        return (
          <Grid container item xs={12}>
            <Grid item display="flex" xs={12}>
              <Grid item xs={3}>
                <Typography textAlign="center" fontSize="14px" fontWeight={600}>
                  {t("snellen_equivalent")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="center" fontWeight={600}>
                  {chartTitle}
                </Typography>
              </Grid>
              <Grid container item xs={3}>
                <Grid item xs={6}>
                  <Typography
                    textAlign="center"
                    fontSize="14px"
                    fontWeight={600}
                  >
                    {t("word_logmar")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    textAlign="center"
                    fontSize="14px"
                    fontWeight={600}
                  >
                    {t("word_index")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item display="flex" xs={3} mb="20px">
              <Grid item xs={6}>
                <Typography textAlign="center" fontSize="14px" fontWeight={600}>
                  20 / {t("word_foot")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="center" fontSize="14px" fontWeight={600}>
                  6 / {t("word_meter")}
                </Typography>
              </Grid>
            </Grid>
            {chartLetters?.map((chartLetter, index) => (
              <Grid container item key={index}>
                {/* Snellen Equivalent values */}
                <Grid item display="flex" alignItems="center" xs={3}>
                  <Grid item xs={6} textAlign="center">
                    <Typography textAlign="center">
                      {chartRows?.[index]?.snellenVisualAcuityScaleFeet ?? "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="center">
                    <Typography textAlign="center">
                      {chartRows?.[index]?.snellenVisualAcuityScaleMetres ??
                        "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={6}>
                  <Grid item xs={12} display="flex" justifyContent="center">
                    {chartLetter?.map((letter, j) => {
                      const value = letter?.value;
                      return (
                        <Grid
                          item
                          key={j}
                          sx={{
                            backgroundImage: letter?.backgroundImage,
                            backgroundSize: letter?.backgroundSize,
                            display: "flex",
                            justifyContent: "center",
                            height: "32px",
                            width: "32px",
                          }}
                        >
                          <Typography
                            display="inline-block"
                            fontSize="24px"
                            color={letter?.color}
                            className={styles.eye_chart_font}
                          >
                            {value}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
                {/* LogMAR and Index values */}
                <Grid container item display="flex" alignItems="center" xs={3}>
                  <Grid item xs={6}>
                    <Typography textAlign="center">
                      {chartRows?.[index]?.logMARVisualAcuityScale ?? "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography textAlign="center">{index + 1}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default BaileyLovieGraph;
